import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as router from '@attain-apps/fe/web/main/lib/router'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as me from '../../../me'
import * as routeBound from '../../../route-bound'
import * as clusters from '../clusters'

import { Api } from './api.service'
import associations from './associations'
import * as effects from './effects'
import type { TypeParams } from './types'

/**
 * Holds the space records that are currently assigned to the cluster. This
 * is what the Cluster Details table displays and what you delete from when
 * removing a record from the cluster.
 *
 * Two collections exist because assigning and viewing have different needs:
 * this one tracks what is already in the cluster (restricted to
 * assignedSpaceIds), while cluster-assignable-records tracks what can still
 * be added (filtered by the cluster's department). Keeping them separate
 * means their filters, selections, and loading states never conflict.
 */
@ngCore.Injectable()
export class Collection extends ec.Collection<TypeParams, typeof associations> {
  constructor() {
    const scenarioContext = ngCore.inject(routeBound.scenario.Context.TOKEN)
    const clustersEc = ngCore.inject(clusters.Collection)
    const schemaFeature = ngCore.inject(dms.schema.Feature)
    const meFeature = ngCore.inject(me.Feature)
    const _router = ngCore.inject(router.Router)

    super({
      api: ngCore.inject(Api),
      associations,
      context$: rxjs
        .combineLatest([
          scenarioContext,
          clustersEc.plugins.currentEntityPlugin.feature.selectors.getId()
            .asObservable,
        ])
        .pipe(
          rxjs.map(([context, clusterId]) => {
            if (!context || !ents.extra.space.Cluster.Id.isId(clusterId)) {
              return undefined
            }

            return { ...context, clusterId }
          }),
        ),
      identify: assignedRecord => assignedRecord.id,
      name: ec.Name.Predefined.SpaceClusterAssignedRecord,
      plugins: {
        additionalFactories: [
          new ds.table.ec.Factory<TypeParams>(collection => ({
            rowConfig: ({ entity: record }) => ({
              iconsConfigs: [
                {
                  class: 'atn-icon atn-trash atn-red',
                  onClick: () => {
                    collection.plugins.editingPlugin.feature.actions.markForDeletion
                      .from(
                        `ClusterAssignedRecords.Table.Row-${record.id}.Icons.Trash.Clicked`,
                      )
                      .dispatch({ id: record.id })
                  },
                },
              ],
              onClick: () => {
                const source = `ClusterAssignedRecords.Table.Row-${record.id}.Clicked`

                const recordsCollection = ec.Collection.instances.get(
                  ec.Name.Predefined.SpaceRecord,
                ).collection

                recordsCollection.plugins.currentEntityPlugin.storage.actions.putInIdLookup
                  .from(source)
                  .dispatch({ value: record.id })

                const url = meFeature.contexts.selectors
                  .getUrlForLink({
                    goto: {
                      navigationId:
                        ents.extra.prebuilt.Page.Id.SpaceRecordDetails,
                    },
                  })
                  .asSignal()

                if (url) {
                  _router.actions.navigate.from(source).dispatch({ url })
                }
              },
            }),
          })),
        ],
      },
    })

    this.feature.registerEffects(
      effects.setAssignedRecordsFilter(this, clustersEc, schemaFeature),
    )
  }
}
